import { NetworkStatus, gql, useQuery } from '@apollo/client';
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconSortAscending from '@apollo/icons/small/IconSortAscending.svg';
import IconSortDescending from '@apollo/icons/small/IconSortDescending.svg';
import {
  Button,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuContent,
  MenuHeader,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  MenuTitle,
  Tooltip,
} from '@apollo/orbit';
import React, { useCallback, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useHistory, useLocation } from 'react-router-dom';

import { useIsAppInBackgroundContext } from 'src/app/appLayout/hooks/useIsAppInBackground';
import { determineLoadingState } from 'src/app/graph/operationsPage/report-pane/section-report/errors-section/federatedErrors/determineLoadingState';
import { insightsRouteConfig } from 'src/app/graph/routes';
import { ClientFilterPopover } from 'src/components/clientFilter/ClientFilterPopover';
import { filterTypes } from 'src/components/common/filterProvider/FilterProvider';
import { useCapabilities } from 'src/hooks/useCapabilities';
import { useSubgraphInsightsFilter } from 'src/hooks/useInsightsFilter';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { TimeRangeOption } from 'src/lib/config/timeRangeOptions';
import { CustomTimeRange, date } from 'src/lib/date/date';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  Ordering,
  SubgraphInsightsListOrderByColumn,
} from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import {
  NormalizedSubgraphsMetricsField,
  isValidSubgraphsMetricsField,
  metricConfig,
} from 'src/lib/metrics';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';
import { mergeQueryParams } from 'src/lib/routing';

import { SubgraphsListBody } from './insightsListBody/SubgraphsListBody';
import { OperationsListItemPlaceholder } from './insightsListItemPlaceholder/OperationsListItemPlaceholder';
import { InsightsNoSearchResults } from './insightsNoSearchResults/InsightsNoSearchResults';
import { SelectedSubgraphsListItem } from './insightsSelectedListItem/SelectedSubgraphsListItem';
import { PaginationControls } from './paginationControls/PaginationControls';
import { SearchFilter } from './searchFilter/SearchFilter';
import { TotalCountBadge } from './totalCountBadge/TotalCountBadge';

export type SelectedSubgraph = {
  name: string;
};

interface SubgraphsListProps {
  range: TimeRangeOption;
  bounds: CustomTimeRange;
  filter: filterTypes;
  graphId: string;
  graphVariant: string;
  clientFilterCount?: number;
  selectedSubgraph?: SelectedSubgraph;
}

export const subgraphsListQuery = gql`
  query SubgraphsListQuery(
    $graphId: ID!
    $name: String!
    $first: Int
    $after: String
    $last: Int
    $before: String
    $from: Timestamp!
    $to: Timestamp!
    $filter: SubgraphInsightsListFilterInput
    $orderBy: SubgraphInsightsListOrderByInput
  ) {
    graph(id: $graphId) {
      id
      variant(name: $name) {
        id
        subgraphInsightsList(
          first: $first
          after: $after
          last: $last
          before: $before
          from: $from
          to: $to
          filter: $filter
          orderBy: $orderBy
        ) {
          subgraphs: nodes {
            clientName
            clientVersion
            fetchCount
            fetchCountPerMin
            fetchErrorPercentage
            fetchesWithErrorsCount
            fetchesWithErrorsCountPerMin
            operationId
            operationName
            operationType
            serviceTimeP50Ms
            serviceTimeP90Ms
            serviceTimeP95Ms
            serviceTimeP99Ms
            subgraphId
            subgraphName
            totalDurationMs
          }
          pageInfo {
            startCursor
            endCursor
          }
          totalCount
        }
      }
    }
  }
`;

const getSubgraphSortColumn = (
  subgraphsSort: string | undefined,
): SubgraphInsightsListOrderByColumn => {
  const colMap: {
    [key in NormalizedSubgraphsMetricsField]: SubgraphInsightsListOrderByColumn;
  } = {
    alphabetically: SubgraphInsightsListOrderByColumn.SUBGRAPH_NAME,
    fetchCount: SubgraphInsightsListOrderByColumn.FETCH_COUNT,
    fetchCountPerMin: SubgraphInsightsListOrderByColumn.FETCH_COUNT_PER_MIN,
    fetchesWithErrorsCount:
      SubgraphInsightsListOrderByColumn.FETCHES_WITH_ERRORS_COUNT,
    fetchesWithErrorsCountPerMin:
      SubgraphInsightsListOrderByColumn.FETCHES_WITH_ERRORS_COUNT_PER_MIN,
    fetchErrorPercentage:
      SubgraphInsightsListOrderByColumn.FETCH_ERROR_PERCENTAGE,
    serviceTimeP50Ms: SubgraphInsightsListOrderByColumn.SERVICE_TIME_P50,
    serviceTimeP90Ms: SubgraphInsightsListOrderByColumn.SERVICE_TIME_P90,
    serviceTimeP95Ms: SubgraphInsightsListOrderByColumn.SERVICE_TIME_P95,
    serviceTimeP99Ms: SubgraphInsightsListOrderByColumn.SERVICE_TIME_P99,
    totalDurationMs: SubgraphInsightsListOrderByColumn.TOTAL_DURATION_MS,
  };

  return subgraphsSort && subgraphsSort in colMap
    ? colMap[subgraphsSort as NormalizedSubgraphsMetricsField]
    : SubgraphInsightsListOrderByColumn.FETCH_COUNT;
};

export const SubgraphsList = (props: SubgraphsListProps) => {
  const { routeParams: filterParams, setFilterParams } =
    useSubgraphInsightsFilter();
  const {
    subgraphsDir,
    subgraphSearch,
    subgraphsAfter,
    subgraphsBefore,
    subgraphsPage = 0,
    subgraphsSort = 'fetchCount',
  } = filterParams;
  const sortDirectionIsDescending =
    subgraphsDir === 'desc' || subgraphsDir === undefined;

  const {
    graphId,
    graphVariant,
    range,
    bounds,
    filter,
    selectedSubgraph,
    clientFilterCount,
  } = props;

  const location = useLocation();
  const history = useHistory();
  const trackCustomEvent = useTrackCustomEvent();
  const isInBackground = useIsAppInBackgroundContext();
  const [inViewRef, inView] = useInView();

  const canFilterOnClients = useCapabilities().capabilities?.clients ?? false;
  const canViewClientVersions =
    useCapabilities().capabilities?.clientVersions ?? false;

  const [subgraphSearchInputValue, setSubgraphSearchInputValue] = useState(
    subgraphSearch ?? '',
  );

  React.useEffect(() => {
    if (!subgraphSearch) {
      setSubgraphSearchInputValue('');
    }
  }, [subgraphSearch]);

  const resetSubgraphSearchInputValue = () => {
    setSubgraphSearchInputValue('');
  };

  const { timeFrom, timeTo, pollInterval } = date.getDateBounds(
    range,
    bounds,
    undefined,
  );

  const sortDirectionLongformToReadableMap = {
    ASCENDING: 'asc' as const,
    DESCENDING: 'desc' as const,
  };

  const statsSortKey = isValidSubgraphsMetricsField(subgraphsSort)
    ? subgraphsSort
    : 'fetchCount';

  const updateSortDirection = (direction: Ordering) => {
    setFilterParams(insightsRouteConfig, {
      subgraphsDir: sortDirectionLongformToReadableMap[direction],
      subgraphsPage: 0,
      subgraphsAfter: undefined,
      subgraphsBefore: undefined,
    });

    trackCustomEvent({
      category: 'Insights L3 Subgraphs List',
      action: 'selected_subgraphs_sort_direction',
      label: direction,
    });
  };

  const updateSortOption = (
    newSubgraphsSort: NormalizedSubgraphsMetricsField,
  ) => {
    const asc = sortDirectionLongformToReadableMap[Ordering.ASCENDING];
    const desc = sortDirectionLongformToReadableMap[Ordering.DESCENDING];
    const prevSubgraphsSort = subgraphsSort;
    // When sorting Alphabetically, sort asc by default. Sort desc for any other
    // sort metric
    const newSubgraphsDir =
      newSubgraphsSort === 'alphabetically'
        ? asc
        : prevSubgraphsSort === 'alphabetically'
          ? desc
          : subgraphsDir;

    setFilterParams(insightsRouteConfig, {
      subgraphsSort: newSubgraphsSort,
      subgraphsDir: newSubgraphsDir,
      subgraphsPage: 0,
      subgraphsAfter: undefined,
      subgraphsBefore: undefined,
    });

    trackCustomEvent({
      category: 'Insights L3 Subgraphs List',
      action: 'selected_insights_subgraph_sort',
      label: newSubgraphsSort,
    });
  };

  const subgraphListPageSize = 50;
  const paginationParams = subgraphsAfter
    ? {
        first: subgraphListPageSize,
        after: subgraphsAfter,
      }
    : subgraphsBefore
      ? {
          last: subgraphListPageSize,
          before: subgraphsBefore,
        }
      : {
          first: subgraphListPageSize,
        };

  const skip = !graphId || !graphVariant || isInBackground || !inView;

  const {
    data: newData,
    previousData,
    networkStatus,
    stopPolling,
  } = useQuery<
    GraphQLTypes.SubgraphsListQuery,
    GraphQLTypes.SubgraphsListQueryVariables
  >(subgraphsListQuery, {
    variables: {
      ...paginationParams,
      graphId,
      name: graphVariant || '',
      from: timeFrom,
      to: timeTo,
      filter: {
        ...filter,
        search: subgraphSearch,
      },
      orderBy: {
        column: getSubgraphSortColumn(subgraphsSort),
        direction:
          subgraphsDir === 'asc' ? Ordering.ASCENDING : Ordering.DESCENDING,
      },
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    pollInterval,
    skip,
    skipPollAttempt: useCallback(() => skip, [skip]),
  });

  const data = newData ?? previousData;
  const totalCount = data?.graph?.variant?.subgraphInsightsList?.totalCount;
  const subgraphsList = data?.graph?.variant?.subgraphInsightsList;

  const loadingState = determineLoadingState({
    hasPreviousData: isMinLengthArray(
      1,
      data?.graph?.variant?.subgraphInsightsList?.subgraphs,
    ),
    networkStatus,
  });

  // We see a "loading" state when returning from the background and
  // "poll" when polling.
  const passiveRefetching = [
    NetworkStatus.loading,
    NetworkStatus.poll,
  ].includes(networkStatus);

  const isSelectedSubgraphInCurrentPage =
    !!selectedSubgraph &&
    !!subgraphsList?.subgraphs?.find(
      (subgraph) => subgraph.subgraphName === selectedSubgraph?.name,
    );

  const numPages =
    (totalCount && Math.ceil(totalCount / subgraphListPageSize) - 1) ?? 1;

  const firstSubgraph = subgraphsList?.subgraphs?.[0];

  // Pick the first subgraph in the list when no subgraph selected
  if (!selectedSubgraph && firstSubgraph) {
    setFilterParams(insightsRouteConfig, {
      subgraphName: firstSubgraph.subgraphName,
    });
  }

  return (
    <div className="flex h-full flex-col" ref={inViewRef}>
      <div className="flex items-center">
        <SearchFilter
          placeholderText="Search for a subgraph"
          inputValue={subgraphSearchInputValue}
          onInputChange={setSubgraphSearchInputValue}
          onSearch={(search: string) => {
            history.replace({
              ...location,
              search: mergeQueryParams(location.search, {
                subgraphSearch: search,
                subgraphsPage: 0,
                subgraphsAfter: undefined,
                subgraphsBefore: undefined,
              }),
            });

            trackCustomEvent({
              category: 'Insights L3 Subgraphs List',
              action: 'executed_subgraphs_search',
            });
          }}
          onClear={() => {
            resetSubgraphSearchInputValue();
            trackCustomEvent({
              category: 'Insights L3 Subgraphs List',
              action: 'cleared_subgraphs_search',
            });
          }}
        />
        {canFilterOnClients && (
          <ClientFilterPopover
            graphId={graphId}
            canViewVersions={canViewClientVersions}
            clientFilterCount={clientFilterCount}
          />
        )}
      </div>
      <div className="mb-2 mt-3 flex items-center justify-between font-semibold text-link">
        <div className="flex flex-1 items-center justify-end">
          <Menu>
            <MenuButton
              as={Button}
              size="sm"
              variant="hidden"
              rightIcon={<IconChevronDown className="ml-1 text-icon-primary" />}
            >
              <span className="truncate">
                {metricConfig.allSubgraphsMetrics[statsSortKey]}
              </span>
            </MenuButton>
            <MenuContent>
              <MenuHeader>
                <MenuTitle>Sort subgraphs by</MenuTitle>
              </MenuHeader>
              <MenuList>
                <MenuOptionGroup
                  type="radio"
                  value={
                    subgraphsSort as Parameters<typeof updateSortOption>[0]
                  }
                  onChange={(key: NormalizedSubgraphsMetricsField) => {
                    updateSortOption(key);
                  }}
                >
                  {Object.entries(metricConfig.allSubgraphsMetrics).map(
                    ([key, value]) => (
                      <MenuItemOption
                        key={key}
                        value={key}
                        type="radio"
                        className="group"
                        description={
                          key === 'alphabetically'
                            ? 'Total fetches will be shown when selected.'
                            : undefined
                        }
                      >
                        {value}
                      </MenuItemOption>
                    ),
                  )}
                </MenuOptionGroup>
              </MenuList>
            </MenuContent>
          </Menu>
        </div>
      </div>
      {((networkStatus === NetworkStatus.loading ||
        networkStatus === NetworkStatus.setVariables) &&
        previousData?.graph?.variant?.subgraphInsightsList?.totalCount === 0) ||
      !data ||
      !data?.graph?.variant ||
      !inView ? (
        <OperationsListItemPlaceholder />
      ) : subgraphsList?.totalCount === 0 ? (
        <div>
          {selectedSubgraph !== undefined &&
          !isSelectedSubgraphInCurrentPage ? (
            <SelectedSubgraphsListItem
              loading={loadingState === 'secondary'}
              {...selectedSubgraph}
            />
          ) : null}
          <InsightsNoSearchResults
            entity="Subgraph"
            resetSearch={resetSubgraphSearchInputValue}
            graphId={graphId}
            graphVariant={graphVariant}
          />
        </div>
      ) : (
        <>
          <div className="hide-scrollbar flex-1 overflow-y-auto overflow-x-hidden border-t-[1px] border-primary">
            <div className="ml-4 mt-4 flex items-center">
              <Heading variant="title" className="mr-1">
                {/* TODO: update this with subgraphsFilter value */}
                Subgraphs
              </Heading>
              <TotalCountBadge count={totalCount} />
              <Tooltip
                label={`Switch to ${
                  sortDirectionIsDescending ? 'Ascending' : 'Descending'
                }`}
              >
                <IconButton
                  size="xs"
                  className="ml-auto mr-2"
                  variant="hidden"
                  aria-label={`Switch to ${
                    sortDirectionIsDescending ? 'Ascending' : 'Descending'
                  }`}
                  onClick={() => {
                    updateSortDirection(
                      sortDirectionIsDescending
                        ? Ordering.ASCENDING
                        : Ordering.DESCENDING,
                    );
                  }}
                >
                  {sortDirectionIsDescending ? (
                    <IconSortDescending />
                  ) : (
                    <IconSortAscending />
                  )}
                </IconButton>
              </Tooltip>
            </div>
            <SubgraphsListBody
              data={data}
              selectedSubgraphName={selectedSubgraph?.name ?? ''}
              statsSortKey={statsSortKey}
              loading={loadingState === 'secondary'}
            />
          </div>

          {totalCount ? (
            <PaginationControls
              currentPage={subgraphsPage}
              totalCount={totalCount}
              pageSize={subgraphListPageSize}
              numPages={numPages}
              loading={passiveRefetching}
              disabled={loadingState === 'secondary' || passiveRefetching}
              onFirst={(nextPage) => {
                history.replace(
                  insightsRouteConfig.locationFrom({
                    location,
                    patch: {
                      subgraphsAfter: RouteConfig.DELETE,
                      subgraphsBefore: RouteConfig.DELETE,
                      subgraphsPage: nextPage,
                      subgraphsPageDir: 'first',
                    },
                  }),
                );

                trackCustomEvent({
                  category: 'Insights L3 Subgraphs List',
                  action: 'selected_first_page',
                });
              }}
              onPrevious={(nextPage) => {
                // If we're already on the first page then get the first page of
                // results w/out the cursor. This allows the user to get to
                // results that may have drifted due paginating over data which
                // is ingested in real-time.
                if (nextPage === 0) {
                  history.replace(
                    insightsRouteConfig.locationFrom({
                      location,
                      patch: {
                        subgraphsAfter: RouteConfig.DELETE,
                        subgraphsBefore: RouteConfig.DELETE,
                        subgraphsPage: nextPage,
                        subgraphsPageDir: 'first',
                      },
                    }),
                  );
                } else {
                  history.replace(
                    insightsRouteConfig.locationFrom({
                      location,
                      patch: {
                        subgraphsAfter: RouteConfig.DELETE,
                        subgraphsBefore:
                          subgraphsList?.pageInfo.startCursor ??
                          RouteConfig.DELETE,
                        subgraphsPage: nextPage,
                        subgraphsPageDir: 'last',
                      },
                    }),
                  );
                }

                trackCustomEvent({
                  category: 'Insights L3 Subgraphs List',
                  action: 'selected_prev_page',
                });
              }}
              onNext={(nextPage) => {
                stopPolling();

                // If we're already on the last page then get the last page of
                // results w/out the cursor. This allows the user to get to
                // results that may have drifted due paginating over data which
                // is ingested in real-time.
                if (nextPage === numPages) {
                  history.replace(
                    insightsRouteConfig.locationFrom({
                      location,
                      patch: {
                        subgraphsAfter: RouteConfig.DELETE,
                        subgraphsBefore: RouteConfig.DELETE,
                        subgraphsPage: nextPage,
                        subgraphsPageDir: 'last',
                      },
                    }),
                  );
                } else {
                  history.replace(
                    insightsRouteConfig.locationFrom({
                      location,
                      patch: {
                        subgraphsAfter: RouteConfig.DELETE,
                        subgraphsBefore:
                          subgraphsList?.pageInfo.endCursor ??
                          RouteConfig.DELETE,
                        subgraphsPage: nextPage,
                        subgraphsPageDir: 'first',
                      },
                    }),
                  );
                }

                trackCustomEvent({
                  category: 'Insights L3 Subgraphs List',
                  action: 'selected_next_page',
                });
              }}
              onLast={(nextPage) => {
                history.replace(
                  insightsRouteConfig.locationFrom({
                    location,
                    patch: {
                      subgraphsAfter: RouteConfig.DELETE,
                      subgraphsBefore: RouteConfig.DELETE,
                      subgraphsPage: nextPage,
                      subgraphsPageDir: 'last',
                    },
                  }),
                );

                trackCustomEvent({
                  category: 'Insights L3 Subgraphs List',
                  action: 'selected_last_page',
                });
              }}
            />
          ) : null}
        </>
      )}
    </div>
  );
};
